import type { ReactNode } from 'react';
import { ICP_RATING_LABELS, RANK_TAB_LABELS, type ProfileBreakdown, type ProfileLineView } from '@nest-recruit/shared';
import { Stars } from '../../../components/Stars';
import { glyphOf } from '../icp-view';
import styles from './ProfileBreakdownView.module.css';

// ADR 0062, mockup M3: "Match 82 ★4 · Top fit", the must-have callout and the profile's lines — must-haves first, then the rest by
// rating. One component for the review card (SCR-01), the candidate's application page (CAND-03) and the sheet's profile block.

const NO_RANK_WORDS: Readonly<Record<ProfileBreakdown['state'], string>> = {
  read: 'Not ranked yet.',
  waiting: 'Waiting for Jev to read the CV: not ranked yet.',
  unreadable: 'Needs a look: the CV could not be read, so read it yourself.',
  not_connected: 'Not ranked yet.',
  not_needed: 'Not ranked yet.',
};
const NOT_CONNECTED_NOTE = 'Jev isn’t connected, so the CV was not read: ranked on the hard facts only.';
const QUEUE_NOTE = 'Order in this queue: top fits that show every must-have first, then by match. Maybe comes after Top fit.';

interface ProfileBreakdownViewProps {
  readonly breakdown: ProfileBreakdown;
  /** SCR-01 says how the queue is ordered under the lines. */
  readonly isQueueNoteShown?: boolean;
}

export function ProfileBreakdownView({ breakdown, isQueueNoteShown = false }: ProfileBreakdownViewProps): ReactNode {
  const musts = breakdown.lines.filter((line) => line.isMust);
  const rest = breakdown.lines.filter((line) => !line.isMust).sort((a, b) => b.rating - a.rating);
  return (
    <div className={styles.root}>
      <Head breakdown={breakdown} />
      <MustCallout breakdown={breakdown} musts={musts} />
      {breakdown.penalty > 0 && breakdown.match !== null && (
        <p className={styles.note}>
          <b>Knockout penalty: −{breakdown.penalty}.</b> This is already taken off the match above ({breakdown.match} shown, {breakdown.match + breakdown.penalty} before).
        </p>
      )}
      {breakdown.state === 'not_connected' && <p className={styles.note}>{NOT_CONNECTED_NOTE}</p>}
      {musts.length > 0 && <Lines title="Must have" lines={musts} missing={breakdown.missingMusts} />}
      {rest.length > 0 && <Lines title={musts.length > 0 ? 'The rest, most important first' : 'Most important first'} lines={rest} missing={breakdown.missingMusts} />}
      {isQueueNoteShown && <p className={styles.fitLine}>{QUEUE_NOTE}</p>}
    </div>
  );
}

function Head({ breakdown }: { readonly breakdown: ProfileBreakdown }): ReactNode {
  const { match, tab, stars } = breakdown;
  if (match === null || tab === null) {
    return (
      <div className={styles.head}>
        <span className={styles.bigNumber}>—</span>
        <span className={styles.headText}>
          <span className={styles.headTitle}>Match</span>
          <span className={styles.fitLine}>{NO_RANK_WORDS[breakdown.state]}</span>
        </span>
      </div>
    );
  }
  return (
    <div className={styles.head}>
      <span className={styles.bigNumber}>{match}</span>
      <span className={styles.headText}>
        <span className={styles.headTitle}>
          Match {match} <Stars value={stars} /> · {RANK_TAB_LABELS[tab]}
        </span>
        <span className={styles.fitLine}>
          {breakdown.fit === null ? 'nothing known yet' : `fit ${breakdown.fit}`} · covers {breakdown.covers}% of the profile
        </span>
      </span>
    </div>
  );
}

function MustCallout({ breakdown, musts }: { readonly breakdown: ProfileBreakdown; readonly musts: readonly ProfileLineView[] }): ReactNode {
  if (breakdown.state === 'unreadable') {
    return (
      <div className={styles.callout} data-tone="attention" role="note">
        <span className={styles.calloutTitle}>Needs a look</span>
        <span>The CV is a scan or has no text. Read it yourself.</span>
      </div>
    );
  }
  if (breakdown.missingMusts.length > 0) {
    return (
      <div className={styles.callout} role="note">
        <span className={styles.calloutTitle}>Missing a must-have: {breakdown.missingMusts.join(', ')}.</span>
        <span>They stay below everyone who shows every must-have.</span>
      </div>
    );
  }
  if (musts.length === 0 || breakdown.match === null) return null;
  return (
    <div className={styles.callout} data-tone="ok" role="note">
      <span className={styles.calloutTitle}>Shows every must-have</span>
      <span>{musts.map((line) => line.text).join(', ')}.</span>
    </div>
  );
}

function Lines({ title, lines, missing }: { readonly title: string; readonly lines: readonly ProfileLineView[]; readonly missing: readonly string[] }): ReactNode {
  return (
    <>
      <h4 className={styles.sectionHead}>{title}</h4>
      <ul className={styles.lines}>
        {lines.map((line) => {
          const glyph = glyphOf(line);
          return (
            <li key={line.itemId} className={styles.line} data-missing={missing.includes(line.text) || undefined}>
              <span className={styles.glyph} data-tone={glyph.tone} role="img" aria-label={glyph.label} title={glyph.label}>
                {glyph.text}
              </span>
              <span className={styles.lineText}>{line.text}</span>
              <span className={styles.lineRating}>{ICP_RATING_LABELS[line.rating]}</span>
              <span className={styles.lineFinding}>{line.detail ?? glyph.label}</span>
            </li>
          );
        })}
      </ul>
    </>
  );
}
